
import React, { useState } from 'react';
import { differenceInDays, isPast, format } from "date-fns";
import { Clock, Package, Search, Trash2, Filter, AlertTriangle, X, RefreshCw } from "lucide-react";
import { InventoryItem } from "@/hooks/useInventoryItems";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface InventoryItemsTableProps {
  items: InventoryItem[];
  isLoading: boolean;
  refreshItems?: () => void;
}

export default function InventoryItemsTable({ items, isLoading, refreshItems }: InventoryItemsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Show 5 items per page
  
  // Get status badge for inventory items
  const getStatusBadge = (expiryDate: string | null, hasExpiry: boolean) => {
    if (!hasExpiry || !expiryDate) return <Badge variant="outline">No Expiry</Badge>;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    
    if (isPast(expiry)) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    const daysUntilExpiry = differenceInDays(expiry, today);
    
    if (daysUntilExpiry <= 2) {
      return <Badge variant="destructive">Alert</Badge>;
    } else if (daysUntilExpiry <= 7) {
      return <Badge variant="warning">Warning</Badge>;
    } else {
      return <Badge variant="success">Good</Badge>;
    }
  };

  // Format expiry date with days left
  const formatExpiryDate = (expiryDate: string | null) => {
    if (!expiryDate) return "No Expiry";
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysLeft = differenceInDays(expiry, today);
    
    if (daysLeft < 0) {
      return `${Math.abs(daysLeft)} days past`;
    } else {
      return `${daysLeft} days left`;
    }
  };
  
  // Delete item function
  const deleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.id) {
        throw new Error("User not authenticated");
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('persona')
        .eq('id', userData.user.id)
        .maybeSingle();
      
      if (profileError) throw profileError;
      
      const persona = profileData?.persona || 'household';
      
      // First delete from the persona-specific table based on user's persona
      if (persona === 'household') {
        const { error: deleteError } = await supabase
          .from('household_inventory')
          .delete()
          .eq('id', itemToDelete.id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'restaurant') {
        const { error: deleteError } = await supabase
          .from('restaurant_inventory')
          .delete()
          .eq('id', itemToDelete.id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'retail') {
        const { error: deleteError } = await supabase
          .from('retail_inventory')
          .delete()
          .eq('id', itemToDelete.id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'healthcare') {
        const { error: deleteError } = await supabase
          .from('healthcare_inventory')
          .delete()
          .eq('id', itemToDelete.id);
          
        if (deleteError) throw deleteError;
      }
      
      // Then delete from main inventory_items table
      const { error: mainDeleteError } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', itemToDelete.id);
        
      if (mainDeleteError) throw mainDeleteError;
      
      toast.success("Item deleted successfully", {
        position: "bottom-right",
        dismissible: true,
      });
      
      // Refresh the items list if the function is provided
      if (refreshItems) refreshItems();
    } catch (error: any) {
      console.error("Error deleting item:", error.message);
      toast.error("Failed to delete item", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Extract unique categories and locations for filters
  const categories = [...new Set(items.map(item => item.category))];
  const locations = [...new Set(items.map(item => item.location).filter(Boolean))] as string[];
  
  // Filter and search items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (item.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter ? item.category === categoryFilter : true;
    const matchesLocation = locationFilter ? item.location === locationFilter : true;
    
    return matchesSearch && matchesCategory && matchesLocation;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // Reset filters
  const resetFilters = () => {
    setCategoryFilter('');
    setLocationFilter('');
    setSearchTerm('');
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // If we have fewer pages than the max, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page
      pageNumbers.push(1);
      
      // Calculate start and end of center group
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if near the start
      if (currentPage <= 3) {
        endPage = Math.min(totalPages - 1, 4);
      }
      
      // Adjust if near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis if needed at the beginning
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
      
      // Add center group of pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis if needed at the end
      if (endPage < totalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      
      // Always include last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-lg flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-expitrack-primary"/>
            <CardTitle className="text-lg">Inventory Items</CardTitle>
          </div>
          <Badge variant="outline" className="bg-white">
            {filteredItems.length}/{items.length} items
          </Badge>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Search items..."
              className="pl-8 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="absolute right-2.5 top-2.5 text-gray-500 hover:text-gray-700"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1">
                <Filter className="h-4 w-4" /> 
                Filter
                {(categoryFilter || locationFilter) && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1 text-xs">
                    {(categoryFilter ? 1 : 0) + (locationFilter ? 1 : 0)}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Filter by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              <div className="p-2">
                <p className="text-xs font-medium mb-1">Category</p>
                <select 
                  className="w-full p-1 text-sm border rounded" 
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((category, i) => (
                    <option key={i} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="p-2">
                <p className="text-xs font-medium mb-1">Location</p>
                <select 
                  className="w-full p-1 text-sm border rounded" 
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {locations.map((location, i) => (
                    <option key={i} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              <DropdownMenuSeparator />
              <div className="p-2">
                <Button variant="outline" size="sm" className="w-full" onClick={resetFilters}>
                  Reset Filters
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {refreshItems && (
            <Button variant="outline" size="icon" onClick={refreshItems}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-expitrack-primary mr-2" />
            <span>Loading items...</span>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8">
            {items.length === 0 ? (
              <>
                <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-3">No inventory items found</p>
                <Link to="/inventory">
                  <Button size="sm" variant="outline">Add Items</Button>
                </Link>
              </>
            ) : (
              <>
                <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-3">No items match your filters</p>
                <Button size="sm" variant="outline" onClick={resetFilters}>Reset Filters</Button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.location || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Clock className="h-3.5 w-3.5 mr-1 text-gray-500" />
                        {formatExpiryDate(item.expiry_date)}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(item.expiry_date, item.has_expiry)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive/90"
                          onClick={() => {
                            setItemToDelete(item);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="py-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    
                    {getPageNumbers().map((page, index) => (
                      <PaginationItem key={index}>
                        {page === 'ellipsis-start' || page === 'ellipsis-end' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink 
                            isActive={currentPage === page}
                            onClick={() => handlePageChange(page as number)}
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
        )}
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteItem}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
